/*
Risk Categories Service

This service handles all API calls for risk categories.
Following established patterns from PROJECT_CONVENTIONS.md:
- Uses axios with withCredentials for session authentication
- Standardized response format conversion
- Error handling with proper status codes
- Base URL configuration
*/

import apiClient from './apiService';
import { AxiosResponse } from 'axios';
import type { AuditRecord } from '../components/common/AuditHistoryModal';

// Use shared apiClient
const apiService = apiClient;

// Types
export interface RiskCategory {
  id: number;
  entity_id: number;
  category_name: string;
  description?: string;
  is_active: number;
  is_approved: number;
  created_by: number;
  created_on: string;
  modified_by?: number;
  modified_on?: string;
  // Approval workflow (migration 101)
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  submitted_by?: number;
  submitted_on?: string;
  approved_by?: number;
  approved_on?: string;
  approval_note?: string;
  rejected_by?: number;
  rejected_on?: string;
  rejection_reason?: string;
  code_seq?: number | null;
  code?: string | null;
}

export interface CategoryQueryParams {
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  search?: string;
  page?: number;
  limit?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  meta?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class RiskCategoriesService {

  /**
   * Get all risk categories with optional filtering
   */
  async getAll(params?: CategoryQueryParams): Promise<RiskCategory[]> {
    try {
      // Build query string from parameters
      const queryParams = new URLSearchParams();
      if (params?.status) {
        queryParams.append('status', params.status);
      }
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.page) {
        queryParams.append('page', params.page.toString());
      }
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }

      const queryString = queryParams.toString();
      const url = `/api/v1/rsk/categories${queryString ? `?${queryString}` : ''}`;

      // Make API call
      const response: AxiosResponse<ApiResponse<RiskCategory[]>> =
        await apiService.get(url);

      // Handle response format conversion
      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch risk categories');
      }
    } catch (error: any) {
      console.error('Error fetching risk categories:', error);
      // Handle 401 Unauthorized - redirect to login
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  /**
   * Get a single risk category by ID
   */
  async getById(categoryId: number): Promise<RiskCategory> {
    try {
      const response: AxiosResponse<ApiResponse<RiskCategory>> =
        await apiService.get(`/api/v1/rsk/categories/${categoryId}`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch risk category');
      }
    } catch (error: any) {
      console.error(`Error fetching risk category ${categoryId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  /**
   * Create a new risk category
   */
  async create(categoryData: Partial<RiskCategory>): Promise<RiskCategory> {
    try {
      const response: AxiosResponse<ApiResponse<RiskCategory>> =
        await apiService.post('/api/v1/rsk/categories', categoryData);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to create risk category');
      }
    } catch (error: any) {
      console.error('Error creating risk category:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  /**
   * Update an existing risk category
   */
  async update(categoryId: number, updateData: Partial<RiskCategory>): Promise<RiskCategory> {
    try {
      const response: AxiosResponse<ApiResponse<RiskCategory>> =
        await apiService.put(`/api/v1/rsk/categories/${categoryId}`, updateData);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to update risk category');
      }
    } catch (error: any) {
      console.error(`Error updating risk category ${categoryId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  /**
   * Delete a risk category (soft delete - sets is_active = -1)
   */
  async delete(categoryId: number): Promise<void> {
    try {
      const url = `/api/v1/rsk/categories/${categoryId}`;
      const response: AxiosResponse<ApiResponse<void>> =
        await apiService.delete(url);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete risk category');
      }
    } catch (error: any) {
      console.error(`Error deleting risk category ${categoryId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  /**
   * Toggle the active status of a risk category (activate/inactivate)
   */
  async toggleStatus(categoryId: number): Promise<RiskCategory> {
    try {
      const response: AxiosResponse<ApiResponse<RiskCategory>> =
        await apiService.patch(`/api/v1/rsk/categories/${categoryId}/toggle`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to toggle category status');
      }
    } catch (error: any) {
      console.error(`Error toggling risk category ${categoryId} status:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  /** Submit a draft/rejected record for approval. */
  async submit(categoryId: number): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`/api/v1/rsk/categories/${categoryId}/submit`);
    if (!res.data.success) throw new Error(res.data.error || 'Submit failed');
  }

  /** Approve a submitted record (approver scope only). */
  async approve(categoryId: number, note?: string): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`/api/v1/rsk/categories/${categoryId}/approve`, { note });
    if (!res.data.success) throw new Error(res.data.error || 'Approve failed');
  }

  /** Reject a submitted record with mandatory reason (approver scope only). */
  async reject(categoryId: number, reason: string): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`/api/v1/rsk/categories/${categoryId}/reject`, { reason });
    if (!res.data.success) throw new Error(res.data.error || 'Reject failed');
  }

  /** Reopen an approved/rejected record back to draft (approver scope only). */
  async reopen(categoryId: number): Promise<void> {
    const res = await apiService.post<ApiResponse<void>>(`/api/v1/rsk/categories/${categoryId}/reopen`);
    if (!res.data.success) throw new Error(res.data.error || 'Reopen failed');
  }

  /** Fetch audit/workflow history for a category. */
  async getHistory(categoryId: number): Promise<AuditRecord[]> {
    try {
      const response = await apiService.get(`/api/v1/rsk/categories/${categoryId}/history`);
      if (response.data.success && response.data.data) {
        return (response.data.data as any[]).map((r: any) => ({
          audit_id: r.id,
          action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
          changed_by_username: r.changed_by_username || 'Unknown',
          changed_on: r.changed_on,
          change_reason: r.remarks,
        }));
      }
      throw new Error(response.data.error || 'Failed to fetch history');
    } catch (error: any) {
      if (error.response?.status === 401) window.location.href = '/login';
      throw error;
    }
  }

  /**
   * Validate category data before submission
   */
  validateData(categoryData: Partial<RiskCategory>, isUpdate: boolean = false): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Required field validation (for create)
    if (!isUpdate) {
      if (!categoryData.category_name || categoryData.category_name.trim() === '') {
        errors.push('Category name is required');
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

// Export singleton instance
const riskCategoriesService = new RiskCategoriesService();
export default riskCategoriesService;
